import './style.scss';

import React, { useState } from 'react';
import { DirectionsRenderer, Marker, Polyline } from 'react-google-maps';
import greenDot from 'src/images/green-map-dot.png';
import { Button, Col, Icon, Row, useApp } from 'straightline-ui';
import { isFTL, STATUS } from 'straightline-utils/constants';
import { formatDistanceToNow } from 'straightline-utils/time';

import Map from '../Map';

const LoadingMap = ({ height = 300 }) => {
  return <div style={{ height }} />;
};

const ErrorMap = ({ height = 300, error }) => {
  return (
    <div
      style={{ height }}
      className="d-flex align-items-center justify-content-center"
    >
      <p className="text-danger">
        {(error && error.message) || 'There was an unexpected error'}
      </p>
    </div>
  );
};

const DirectionsMap = ({ expanded, toggleExpanded, route }) => {
  return (
    <React.Fragment>
      <Map
        containerClassName={`map-header-container ${expanded ? 'open' : ''}`}
      >
        <DirectionsRenderer directions={route.directions} />
      </Map>
      <Expand expanded={expanded} onClick={toggleExpanded} />
    </React.Fragment>
  );
};

const LinesMap = ({ expanded, toggleExpanded, children, ...rest }) => {
  return (
    <React.Fragment>
      <Map
        containerClassName={`map-header-container ${expanded ? 'open' : ''}`}
        {...rest}
      >
        {children}
      </Map>
      <Expand expanded={expanded} onClick={toggleExpanded} />
    </React.Fragment>
  );
};

const LiveTracking = ({ currentLocation }) => {
  return (
    <div className="bg-white p-3 rounded shadow mb-2">
      <Row className="align-items-center">
        <Col xs="auto">
          <img src={greenDot} alt="Green Dot" />
        </Col>
        <Col xs="auto" className="pl-0">
          <p className="font-weight-bold mb-0">Live Tracking</p>
          <p className="mb-0">
            {formatDistanceToNow(currentLocation.carrier_timestamp)}
          </p>
        </Col>
      </Row>
    </div>
  );
};

const ReCenter = ({ onClick }) => {
  return (
    <Button
      color="white"
      size="sm"
      className="shadow animated slideInLeft faster"
      onClick={onClick}
    >
      <Icon type="target" className="text-success font-weight-bold mr-2" />
      Recenter
    </Button>
  );
};

const Expand = ({ onClick, expanded }) => {
  return (
    <Button
      color="white"
      size="sm"
      className="btn-collapse shadow"
      onClick={onClick}
    >
      {expanded ? 'Collapse' : 'Expand'}
    </Button>
  );
};

// Used for shipment previews where this is attached because
// the user is not authed to call api/shipment-routes
function useShipmentRoute(shipment) {
  const app = useApp();

  const result = app.service('api/shipment-routes').useFind(
    {
      query: {
        pickup_address: shipment.pickup_address,
        delivery_address: shipment.delivery_address
      }
    },
    {
      keepPreviousData: true,
      enabled: !shipment.route
    }
  );

  if (!shipment.route) {
    return result;
  }

  return {
    result: shipment.route,
    isLoading: false,
    isError: false
  };
}

function useShipmentTracking(shipment, limit) {
  const app = useApp();

  const result = app.service('api/shipment-tracking').useFind(
    {
      query: {
        shipment_id: shipment.id,
        carrier_id: shipment.carrier_id,
        latitude: { $ne: null },
        longitude: { $ne: null },
        $sort: { carrier_timestamp: -1 },
        $limit: limit
      }
    },
    {
      keepPreviousData: true,
      refetchInterval: 1000 * 60 * 5,
      enabled: !shipment.tracking
    }
  );

  if (!shipment.tracking) {
    return result;
  }

  return {
    result: {
      data: shipment.tracking,
      total: shipment.tracking.length
    },
    isLoading: false,
    isError: false
  };
}

// Note the only difference between InTransitMap and
// PrePickupMap is the DirectionsRendered color
// options
const InTransitDrivingMap = ({ shipment }) => {
  const [dragged, setDragged] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded(!expanded);

  let {
    result: route,
    isLoading: isLoadingRoute,
    isError: isErrorRoute,
    error: errorRoute
  } = useShipmentRoute(shipment);

  let {
    result: tracking,
    isLoading: isLoadingLocation,
    isError: isErrorLocation,
    error: errorLocation
  } = useShipmentTracking(shipment, 1);

  const setIsDragged = () => {
    if (!dragged) {
      setDragged(true);
    }
  };

  const unsetDragged = () => {
    setDragged(false);
  };

  if (isLoadingRoute || isLoadingLocation) {
    return <LoadingMap />;
  }

  if (isErrorRoute || isErrorLocation) {
    return <ErrorMap error={errorRoute || errorLocation} />;
  }

  const currentLocation = tracking?.data[0];

  if (!currentLocation) {
    return (
      <DirectionsMap
        expanded={expanded}
        toggleExpanded={toggleExpanded}
        route={route}
      />
    );
  }

  return (
    <React.Fragment>
      <Map
        containerClassName={`map-header-container ${expanded ? 'open' : ''}`}
        center={dragged ? undefined : currentLocation.position}
        zoom={dragged ? undefined : 13}
        onDrag={setIsDragged}
      >
        {route && (
          <DirectionsRenderer
            options={{
              preserveViewport: true,
              polylineOptions: {
                strokeColor: '#00d97e',
                strokeWeight: 6,
                strokeOpacity: 0.6,
                zIndex: 100
              }
            }}
            directions={route.directions}
          />
        )}
        <Marker
          position={currentLocation.position}
          icon={greenDot}
          title="Current Location"
        />
      </Map>
      <div className="controls">
        <LiveTracking currentLocation={currentLocation} />
        {dragged && <ReCenter onClick={unsetDragged} />}
      </div>
      <Expand expanded={expanded} onClick={toggleExpanded} />
    </React.Fragment>
  );
};

const PrePickupDrivingMap = ({ shipment }) => {
  const [dragged, setDragged] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded(!expanded);

  let {
    result: route,
    isLoading: isLoadingRoute,
    isError: isErrorRoute,
    error: errorRoute
  } = useShipmentRoute(shipment);

  let {
    result: tracking,
    isLoading: isLoadingLocation,
    isError: isErrorLocation,
    error: errorLocation
  } = useShipmentTracking(shipment, 1);

  const setIsDragged = () => {
    if (!dragged) {
      setDragged(true);
    }
  };

  const unsetDragged = () => {
    setDragged(false);
  };

  if (isLoadingRoute || isLoadingLocation) {
    return <LoadingMap />;
  }

  if (isErrorRoute || isErrorLocation) {
    return <ErrorMap error={errorRoute || errorLocation} />;
  }

  const currentLocation = tracking?.data[0];

  if (!currentLocation) {
    return (
      <DirectionsMap
        expanded={expanded}
        toggleExpanded={toggleExpanded}
        route={route}
      />
    );
  }

  return (
    <React.Fragment>
      <Map
        containerClassName={`map-header-container ${expanded ? 'open' : ''}`}
        center={dragged ? undefined : currentLocation.position}
        zoom={dragged ? undefined : 13}
        onDrag={setIsDragged}
      >
        {route && (
          <DirectionsRenderer
            options={{ preserveViewport: true }}
            directions={route.directions}
          />
        )}
        <Marker
          position={currentLocation.position}
          icon={greenDot}
          title="Current Location"
        />
      </Map>
      <div className="controls">
        <LiveTracking currentLocation={currentLocation} />
        {dragged && <ReCenter onClick={unsetDragged} />}
      </div>
      <Expand expanded={expanded} onClick={toggleExpanded} />
    </React.Fragment>
  );
};

const DefaultDrivingMap = ({ shipment }) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded(!expanded);

  const {
    result: route,
    isLoading,
    isError,
    error
  } = useShipmentRoute(shipment);

  if (isLoading) {
    return <LoadingMap />;
  }

  if (isError) {
    return <ErrorMap error={error} />;
  }

  return (
    <DirectionsMap
      expanded={expanded}
      toggleExpanded={toggleExpanded}
      route={route}
    />
  );
};

const DefaultLineMap = ({ shipment }) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(!expanded);
  const center = [
    shipment.pickup_address.position,
    shipment.delivery_address.position
  ];

  return (
    <LinesMap
      expanded={expanded}
      toggleExpanded={toggleExpanded}
      center={center}
    >
      <Marker
        position={shipment.pickup_address.position}
        color="red"
        label={{
          color: '#FFFFFF',
          text: 'A'
        }}
      />
      <Marker
        position={shipment.delivery_address.position}
        color="red"
        label={{
          color: '#FFFFFF',
          text: 'B'
        }}
      />
      <Polyline
        options={{
          geodesic: true,
          path: [
            shipment.pickup_address.position,
            shipment.delivery_address.position
          ],
          strokeColor: '#4A89F3',
          strokeOpacity: 1,
          strokeWeight: 6
        }}
      />
    </LinesMap>
  );
};

const InTransitLineMap = ({ shipment }) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(!expanded);

  const {
    result: tracking,
    isLoading: isLoadingTracking,
    isError: isErrorTracking,
    error: errorTracking
  } = useShipmentTracking(shipment, 100);

  if (isLoadingTracking) {
    return <LoadingMap />;
  }

  if (isErrorTracking) {
    return <ErrorMap error={errorTracking} />;
  }

  const data = tracking.data.map((track) => {
    return {
      lat: track.latitude,
      lng: track.longitude
    };
  });

  const allMarkers = [
    shipment.delivery_address.position,
    ...data,
    shipment.pickup_address.position
  ];

  const center = allMarkers.slice(0, 3);

  const path = allMarkers.length > 2 ? allMarkers.slice(1) : allMarkers;

  const lastPath =
    allMarkers.length > 2 ? [allMarkers[0], allMarkers[1]] : null;

  const lineSymbol = {
    // console.log(window.google.maps.SymbolPath.CIRCLE);
    // path: window.google.maps.SymbolPath.CIRCLE,
    path: 0,
    fillOpacity: 1,
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: '#ffffff',
    strokeColor: '#4A89F3'
  };

  return (
    <LinesMap
      expanded={expanded}
      toggleExpanded={toggleExpanded}
      center={center}
    >
      <Marker
        position={shipment.pickup_address.position}
        color="red"
        label={{
          color: '#FFFFFF',
          text: 'A'
        }}
      />
      <Marker
        position={shipment.delivery_address.position}
        color="red"
        label={{
          color: '#FFFFFF',
          text: 'B'
        }}
      />
      {data.map((track) => {
        return <Marker position={track} color="red" />;
      })}
      <Polyline
        options={{
          path,
          geodesic: true,
          strokeColor: '#4A89F3',
          strokeOpacity: 1,
          strokeWeight: 6
        }}
      />
      {lastPath && (
        <Polyline
          options={{
            path: lastPath,
            geodesic: true,
            strokeColor: '#92b8f8',
            strokeOpacity: 1,
            strokeWeight: 6,
            icons: [
              {
                icon: lineSymbol,
                offset: '0',
                repeat: '20px'
              }
            ]
          }}
        />
      )}
    </LinesMap>
  );
};

const drivingMaps = {
  [STATUS.MATCHED]: DefaultDrivingMap,
  [STATUS.BOOKED]: DefaultDrivingMap,
  [STATUS.DISPATCHED]: PrePickupDrivingMap,
  [STATUS.CANCELLED]: DefaultDrivingMap,
  [STATUS.PICKUP]: PrePickupDrivingMap,
  [STATUS.TRANSIT]: InTransitDrivingMap,
  [STATUS.DELIVERY]: InTransitDrivingMap,
  [STATUS.DELIVERED]: DefaultDrivingMap
};

const lineMaps = {
  [STATUS.MATCHED]: DefaultLineMap,
  [STATUS.BOOKED]: DefaultLineMap,
  [STATUS.DISPATCHED]: InTransitLineMap,
  [STATUS.CANCELLED]: DefaultLineMap,
  [STATUS.PICKUP]: InTransitLineMap,
  [STATUS.TRANSIT]: InTransitLineMap,
  [STATUS.DELIVERY]: InTransitLineMap,
  [STATUS.DELIVERED]: InTransitLineMap
};

export default function MapHeader(props) {
  const { mode, status_type } = props.shipment;
  const StatusMap = isFTL(mode)
    ? drivingMaps[status_type]
    : lineMaps[status_type];
  return (
    <div className="map-header">
      <StatusMap {...props} />
    </div>
  );
}
